<script lang="ts">
  import { untrack } from 'svelte';
  import ModalShell from './ModalShell.svelte';
  import { createGroup, updateGroup } from './api';
  import { GROUP_TINTS, type GroupView, type GroupTint } from '~/shared/api.js';

  let {
    tripToken,
    existingGroup,
    existingTints = [],
    onSaved,
    onClose,
  }: {
    tripToken: string;
    existingGroup?: GroupView;
    existingTints?: GroupTint[];
    onSaved: (group: GroupView) => void;
    onClose: () => void;
  } = $props();

  const isEditing = $derived(existingGroup !== undefined);

  function pickInitialTint(): GroupTint {
    const used = new Set(existingTints);
    const next = GROUP_TINTS.find((t) => !used.has(t));
    return next ?? GROUP_TINTS[0]!;
  }

  let name: string = $state(untrack(() => existingGroup?.name ?? ''));
  let tint: GroupTint = $state(untrack(() => existingGroup?.tint ?? pickInitialTint()));
  let saving: boolean = $state(false);
  let error: string | null = $state(null);
  let inputEl: HTMLInputElement | null = $state(null);

  const saveLabel = $derived(
    saving
      ? isEditing ? 'Saving…' : 'Creating…'
      : isEditing ? 'Save changes' : 'Create group',
  );

  $effect(() => {
    inputEl?.focus();
  });

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      error = 'Name your group';
      return;
    }
    saving = true;
    error = null;
    try {
      const group = isEditing
        ? await updateGroup(tripToken, existingGroup!.id, { name: trimmed, tint })
        : await createGroup(tripToken, trimmed, tint);
      onSaved(group);
    } catch (e) {
      error = e instanceof Error ? e.message : 'Could not save group';
      saving = false;
    }
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') save();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<ModalShell title={isEditing ? 'Edit group' : 'New group'} {onClose}>
  <label class="field-group">
    <span class="label">Name</span>
    <input
      bind:this={inputEl}
      type="text"
      bind:value={name}
      placeholder="e.g. Cafés, Day 1, Cozy spots"
      maxlength="80"
      autocomplete="off"
      autocapitalize="off"
      autocorrect="off"
      spellcheck="false"
    />
  </label>

  <div class="tints" role="group" aria-label="Color">
    <span class="label">Color</span>
    <div class="dots">
      {#each GROUP_TINTS as t}
        <button
          type="button"
          class="dot-btn"
          class:active={tint === t}
          aria-label={t}
          aria-pressed={tint === t}
          onclick={() => (tint = t)}
        >
          <span class="dot dot--{t}"></span>
        </button>
      {/each}
    </div>
  </div>

  {#if error}
    <p class="err">{error}</p>
  {/if}

  <div class="actions">
    <button class="btn btn-secondary" type="button" onclick={onClose}>Cancel</button>
    <button class="btn btn-primary" type="button" onclick={save} disabled={saving}>
      {saveLabel}
    </button>
  </div>
</ModalShell>

<style>
  .field-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }
  .label {
    font-size: 13px;
    font-weight: 500;
    color: var(--color-charcoal);
  }
  input[type='text'] {
    height: 44px;
    padding: 0 0.875rem;
    background: var(--color-canvas);
    border: 1px solid var(--color-hairline-strong);
    border-radius: var(--radius-sm);
    font-family: inherit;
    /* 16px to avoid iOS Safari auto-zoom-on-focus. */
    font-size: 16px;
    color: var(--color-ink);
  }
  input[type='text']:focus {
    outline: none;
    border-color: var(--color-ink);
    box-shadow: inset 0 0 0 1px var(--color-ink);
  }
  .tints {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .dots {
    display: flex;
    gap: 0.5rem;
  }
  .dot-btn {
    background: transparent;
    border: 2px solid transparent;
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: border-color 100ms ease;
  }
  .dot-btn:hover { border-color: var(--color-hairline); }
  .dot-btn.active { border-color: var(--color-ink); }
  .dot-btn .dot {
    width: 18px;
    height: 18px;
  }
  .err {
    margin: 0;
    font-size: 13px;
    color: var(--color-error);
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
</style>
